import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'antd';
import { DownOutlined, UpOutlined, CheckOutlined } from '@ant-design/icons';
import { MULTICHOIX_STEP_STATUS } from '../../constants/multichoixSteps';

const getStatusStyles = (status) => {
  switch (status) {
    case MULTICHOIX_STEP_STATUS.COMPLETED:
      return {
        container: 'bg-[#f8fdec] border-[#d3f261]',
        numberBg: 'bg-[#7cb305] border-[#7cb305]',
        numberText: 'text-white',
        titleText: 'text-[rgba(0,0,0,0.85)] font-bold',
      };
    case MULTICHOIX_STEP_STATUS.ACTIVE:
      return {
        container: 'bg-[#f8fdec] border-[#7cb305]',
        numberBg: 'bg-[#7cb305] border-[#7cb305]',
        numberText: 'text-white',
        titleText: 'text-[rgba(0,0,0,0.85)] font-bold',
      };
    case MULTICHOIX_STEP_STATUS.AVAILABLE:
      return {
        container: 'bg-white border-[#d9d9d9]',
        numberBg: 'bg-white border-[#7cb305]',
        numberText: 'text-[#7cb305]',
        titleText: 'text-[rgba(0,0,0,0.85)]',
      };
    default:
      return {
        container: 'bg-[#fafafa] border-[#d9d9d9]',
        numberBg: 'bg-[#d9d9d9] border-[#d9d9d9]',
        numberText: 'text-white',
        titleText: 'text-[#d9d9d9]',
      };
  }
};

const MultichoixStepHeader = ({
  step,
  status,
  isActive,
  canClick,
  selections,
  selectionLabels,
  isCompleted,
  onClick,
}) => {
  const { t } = useTranslation('treatment-search');
  const styles = getStatusStyles(status);

  const renderSelectedTags = () => {
    // Don't show tags for enemies step (step 4) - it has its own "selected elements" display inside
    if (step.componentType === 'enemiesComplex') return null;

    if (!selectionLabels || selectionLabels.length === 0) return null;

    const maxTags = 2;
    const visibleTags = selectionLabels.slice(0, maxTags);
    const remainingCount = selectionLabels.length - maxTags;

    return (
      <>
        {/* Desktop: Show tags */}
        <div className="hidden sm:flex flex-wrap gap-1 mr-2">
          {visibleTags.map((item, index) => (
            <Tag
              key={item.value || item.id || index}
              className="bg-[#5b8c00] border-[#5b8c00] text-white text-xs rounded px-2 py-0.5 max-w-[120px] lg:max-w-[200px] truncate"
            >
              {item.label || item.name || item.value}
            </Tag>
          ))}
          {remainingCount > 0 && (
            <Tag className="bg-[#5b8c00] border-[#5b8c00] text-white text-xs rounded px-2 py-0.5">
              +{remainingCount}
            </Tag>
          )}
        </div>
        {/* Mobile: Show count only */}
        <div className="flex sm:hidden mr-2">
          <Tag className="bg-[#5b8c00] border-[#5b8c00] text-white text-xs rounded px-2 py-0.5">
            {selectionLabels.length}
          </Tag>
        </div>
      </>
    );
  };

  return (
    <div
      className={`
        rounded-t-[16px] border p-3 sm:p-6 cursor-pointer transition-all
        ${styles.container}
        ${!isActive ? 'rounded-b-[16px]' : ''}
        ${canClick ? 'hover:shadow-md' : 'cursor-not-allowed'}
      `}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <div
          className={`
            w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border flex-shrink-0 text-xs sm:text-sm
            ${styles.numberBg} ${styles.numberText}
          `}
        >
          {isCompleted ? <CheckOutlined className="text-xs sm:text-sm" /> : step.key}
        </div>
        <span className={`text-sm sm:text-base truncate ${styles.titleText}`}>{t(step.title)}</span>
        {/* Tags and arrow wrapper - always pushed to the right */}
        <div className="ml-auto flex items-center flex-shrink-0">
          {renderSelectedTags()}
          <div className={!canClick ? 'opacity-30' : ''}>
            {isActive ? (
              <UpOutlined className="text-gray-600 text-xs sm:text-sm" />
            ) : (
              <DownOutlined className="text-gray-600 text-xs sm:text-sm" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultichoixStepHeader;
